import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from 'api/axios';

const WsContext = createContext(null);

const UNREAD_LS_KEY = 'chat_unreadByKey_v2';
const LATEST_LS_KEY = 'chat_latestByKey_v2';
const META_LS_KEY = 'chat_metaByKey_v2';

// metaByKey[key] = {
//   justRead?: boolean,
//   serverAt?: number
// }

function safeParseLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : fallback;
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function safeSetLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function extractPreview(raw) {
  const m = raw?.message || {};
  const txt = m.body ?? m.text ?? m.caption ?? raw?.message ?? raw?.text ?? '';
  return typeof txt === 'string' ? txt.trim() : '';
}

function inferDirection(raw) {
  const m = raw?.message || {};
  const fromMe =
    raw?.fromMe === true ||
    m?.fromMe === true ||
    raw?.key?.fromMe === true ||
    m?.key?.fromMe === true ||
    raw?.isFromMe === true ||
    m?.isFromMe === true;

  if (raw?.direction === 'outgoing') return 'outgoing';
  if (raw?.direction === 'incoming') return 'incoming';
  if (fromMe) return 'outgoing';
  return 'incoming';
}

function normalizeWsMessage(raw) {
  const sessionName = raw?.sessionName || '';
  const direction = inferDirection(raw);
  const m = raw?.message || {};

  const messageId = m.id || raw?.messageId || '';
  const from = m.from || raw?.fromWaId || raw?.from || '';
  const to = m.to || raw?.toNumber || raw?.to || '';

  const chatId = direction === 'incoming' ? from : to;

  const tsSec = typeof m.timestamp === 'number' ? m.timestamp : typeof m.t === 'number' ? m.t : null;
  const createdAt = raw?.createdAt || m.createdAt || (tsSec ? new Date(tsSec * 1000).toISOString() : new Date().toISOString());

  const createdAtMs = (() => {
    const t = Date.parse(createdAt);
    return Number.isFinite(t) ? t : Date.now();
  })();

  return {
    id: raw?.id || messageId || Date.now(),
    messageId,
    sessionName,
    chatId,
    direction,
    createdAt,
    createdAtMs,
    raw
  };
}

export function WsProvider({ children }) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  const [isConnected, setIsConnected] = useState(false);

  const [unreadByKey, setUnreadByKey] = useState(() => safeParseLS(UNREAD_LS_KEY, {}));
  const [latestByKey, setLatestByKey] = useState(() => safeParseLS(LATEST_LS_KEY, {}));
  const [metaByKey, setMetaByKey] = useState(() => safeParseLS(META_LS_KEY, {}));

  const metaRef = useRef(metaByKey);
  useEffect(() => {
    metaRef.current = metaByKey;
  }, [metaByKey]);

  const lidMapRef = useRef(new Map());
  const activeChatKeyRef = useRef(null);

  const WS_URL = useMemo(() => {
    const host = window.location.hostname;
    return `ws://${host}:3000/ws/messages`;
  }, []);

  useEffect(() => safeSetLS(UNREAD_LS_KEY, unreadByKey || {}), [unreadByKey]);
  useEffect(() => safeSetLS(LATEST_LS_KEY, latestByKey || {}), [latestByKey]);
  useEffect(() => safeSetLS(META_LS_KEY, metaByKey || {}), [metaByKey]);

  const resolveChatId = useCallback(async (sessionName, wsChatId) => {
    if (!sessionName || !wsChatId) return null;

    const cacheKey = `${sessionName}|${wsChatId}`;
    const cached = lidMapRef.current.get(cacheKey);
    if (cached) return cached;

    try {
      const ss = encodeURIComponent(sessionName);
      const digits = String(wsChatId).replace('@lid', '').replace('@c.us', '');
      const chatIdParam = encodeURIComponent(digits);

      const res = await api.get(`/api/messages/chats?sessionName=${ss}&chatId=${chatIdParam}`, {
        withCredentials: true,
        headers: { 'X-API-Key': 'secret123' }
      });

      const payload = res?.data || {};
      const list = Array.isArray(payload.chats) ? payload.chats : [];
      const found = list.find((x) => String(x?.chatId || '').includes('@c.us')) || list[0];
      const resolved = found?.chatId ? String(found.chatId) : null;

      if (resolved) lidMapRef.current.set(cacheKey, resolved);
      return resolved;
    } catch {
      return null;
    }
  }, []);

  const normalizeChatId = useCallback(
    async (sessionName, chatId) => {
      if (!sessionName || !chatId) return null;
      if (!String(chatId).includes('@lid')) return String(chatId);

      const resolved = await resolveChatId(sessionName, chatId);
      return resolved || null;
    },
    [resolveChatId]
  );

  const setMetaForKey = useCallback((key, updater) => {
    setMetaByKey((prev) => {
      const current = prev?.[key] || {};
      const nextItem = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      const next = { ...(prev || {}), [key]: nextItem };
      metaRef.current = next;
      return next;
    });
  }, []);

  const setActiveChat = useCallback((sessionName, chatId) => {
    if (!sessionName || !chatId) {
      activeChatKeyRef.current = null;
      return;
    }
    activeChatKeyRef.current = `${sessionName}|${chatId}`;
  }, []);

  const markRead = useCallback(
    (sessionName, chatId) => {
      if (!sessionName || !chatId) return;

      const key = `${sessionName}|${chatId}`;

      setUnreadByKey((prev) => ({ ...(prev || {}), [key]: 0 }));

      setMetaForKey(key, (cur) => ({
        ...cur,
        justRead: true
      }));
    },
    [setMetaForKey]
  );

  const clearAllUnread = useCallback(() => {
    setUnreadByKey((prev) => {
      const next = { ...(prev || {}) };
      for (const k of Object.keys(next)) next[k] = 0;
      return next;
    });

    setMetaByKey((prev) => {
      const next = { ...(prev || {}) };
      for (const k of Object.keys(next)) {
        next[k] = {
          ...(next[k] || {}),
          justRead: true
        };
      }
      metaRef.current = next;
      return next;
    });
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setIsConnected(true);
    };

    ws.onmessage = async (evt) => {
      let raw;
      try {
        raw = JSON.parse(evt.data);
      } catch {
        return;
      }

      if (raw?.type === 'connected') return;

      if (raw?.type === 'chat_unread') {
        const sessionName = raw?.sessionName || '';
        const wsChatId = raw?.chatId || '';
        const serverUnread = Number(raw?.unread || 0);

        if (!sessionName || !wsChatId) return;

        const effectiveChatId = await normalizeChatId(sessionName, wsChatId);
        if (!effectiveChatId) return;

        const key = `${sessionName}|${effectiveChatId}`;
        const now = Date.now();

        const meta = metaRef.current?.[key] || {};
        const justRead = meta.justRead === true;

        setMetaForKey(key, (cur) => ({
          ...cur,
          serverAt: now
        }));

        setUnreadByKey((prev) => {
          const curVal = Number(prev?.[key] || 0);

          if (justRead) {
            if (serverUnread <= 0) {
              setMetaForKey(key, (cur) => ({
                ...cur,
                justRead: false
              }));
              return { ...(prev || {}), [key]: 0 };
            }

            setMetaForKey(key, (cur) => ({
              ...cur,
              justRead: false
            }));
            return { ...(prev || {}), [key]: 1 };
          }

          const nextVal = Math.max(curVal, serverUnread);
          return { ...(prev || {}), [key]: nextVal };
        });

        setLatestByKey((prev) => {
          const next = { ...(prev || {}) };
          next[key] = {
            sessionName,
            chatId: effectiveChatId,
            preview: next[key]?.preview || '',
            ts: now
          };
          return next;
        });

        return;
      }

      const msg = normalizeWsMessage(raw);
      if (!msg.sessionName || !msg.chatId) return;

      let effectiveChatId = msg.chatId;
      if (String(effectiveChatId).includes('@lid')) {
        const resolved = await normalizeChatId(msg.sessionName, effectiveChatId);
        if (!resolved) return;
        effectiveChatId = resolved;
      }

      const key = `${msg.sessionName}|${effectiveChatId}`;
      const now = Date.now();

      const preview = extractPreview(raw);
      setLatestByKey((prev) => {
        const next = { ...(prev || {}) };
        next[key] = {
          sessionName: msg.sessionName,
          chatId: effectiveChatId,
          preview: preview || next[key]?.preview || '',
          ts: now
        };
        return next;
      });

      if (msg.direction !== 'incoming') return;

      const activeKey = activeChatKeyRef.current;

      if (activeKey && activeKey === key) {
        setUnreadByKey((prev) => ({ ...(prev || {}), [key]: 0 }));
        setMetaForKey(key, (cur) => ({
          ...cur,
          justRead: true
        }));
        return;
      }

      const meta = metaRef.current?.[key] || {};
      const justRead = meta.justRead === true;

      setUnreadByKey((prev) => {
        if (justRead) {
          return { ...(prev || {}), [key]: 1 };
        }

        const cur = Number(prev?.[key] || 0);
        return { ...(prev || {}), [key]: cur + 1 };
      });

      setMetaForKey(key, (cur) => ({
        ...cur,
        justRead: false
      }));
    };

    ws.onclose = () => {
      setIsConnected(false);
      const retry = retryRef.current + 1;
      retryRef.current = retry;
      const delay = Math.min(1000 * Math.pow(2, Math.min(retry, 5)), 15000);
      setTimeout(() => connect(), delay);
    };

    ws.onerror = () => {};
  }, [WS_URL, normalizeChatId, setMetaForKey]);

  useEffect(() => {
    connect();
    return () => {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
  }, [connect]);

  const value = useMemo(
    () => ({
      isConnected,
      unreadByKey,
      latestByKey,
      metaByKey,
      setActiveChat,
      markRead,
      clearAllUnread
    }),
    [isConnected, unreadByKey, latestByKey, metaByKey, setActiveChat, markRead, clearAllUnread]
  );

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

export function useWs() {
  const ctx = useContext(WsContext);
  if (!ctx) throw new Error('useWs must be used inside <WsProvider />');
  return ctx;
}
